import React, { useState, useEffect, useContext } from 'react';
import { AppBar, Toolbar, MenuItem, IconButton, Menu, CssBaseline, Link } from '@material-ui/core';
import { fade, makeStyles, createMuiTheme } from '@material-ui/core/styles';
import { ThemeProvider } from '@material-ui/styles';
import MoreIcon from '@material-ui/icons/MoreVert';
import { GlobalTheme } from '../layouts/theme';
import { Theme } from '../components/contexts/themeContext';
import { Context } from '../components/contexts/siteContext.js';
import { Streak } from '../components/contexts/streakContext.js';
import { User } from '../components/contexts/userContext.js';
import { Modules } from '../components/contexts/modulesContext';
import { AppSettingMenu } from '../components/contexts/appSettingMenuContext';
import { SystemVariable } from '../components/contexts/systemVariableContext';
import { Weather } from "../components/contexts/weatherContext";
import { Offline } from "../components/contexts/offlineContext";
import Home from '../components/home/home.js';
import SiteList from '../components/header/siteList'
import Grid from '@material-ui/core/Grid';
import UserAvatar from '../components/header/userAvatar';


const useStyles = makeStyles(theme => ({
    root: {
        padding: theme.spacing(1),
        paddingTop: "30px"
    },
    grow: {
        flexGrow: 1
    },
    menuButton: {
        marginRight: theme.spacing(2)
    },
    title: {
        display: 'none',
        [theme.breakpoints.up('sm')]: {
            display: 'block'
        }
    },
    search: {
        position: 'relative',
        borderRadius: theme.shape.borderRadius,
        backgroundColor: fade(theme.palette.common.white, 0.15),
        '&:hover': {
            backgroundColor: fade(theme.palette.common.white, 0.25)
        },
        marginRight: theme.spacing(2),
        marginLeft: '0px!important',
        width: '100%',
        [theme.breakpoints.up('sm')]: {
            marginLeft: theme.spacing(3),
            width: 'auto'
        }
    },
    inputRoot: {
        color: 'inherit'
    },
    inputInput: {
        padding: theme.spacing(1, 1, 1, 7),
        transition: theme.transitions.create('width'),
        width: '100%',
        [theme.breakpoints.up('md')]: {
            width: 200
        }
    },
    sectionDesktop: {
        display: 'none',
        [theme.breakpoints.up('md')]: {
            display: 'flex'
        }
    },
    sectionMobile: {
        display: 'flex',
        [theme.breakpoints.up('md')]: {
            display: 'none'
        }
    },
    userInfo: {
        float: 'right', paddingRight: '20px', display: 'flex', alignItems: 'center',
        [theme.breakpoints.down('xs')]: {
            marginLeft: '20px',
            float: 'left'
        }
    },
    banner: {
        backgroundColor: 'yellow',
        fontSize: 'medium',
        textAlign: 'center',
        padding: '5px',
        color: 'red',
        cursor: 'pointer'
    }
}));

function MainLayout(props) {
    const classes = useStyles();
    const { defaultSite, siteLoading } = useContext(Context);
    const { theme, setTheme } = useContext(Theme);
    const { modules, getUserModules, trainingModules, getTrainingModules, trainingMode, setTrainingMode } = useContext(Modules);
    const { userInfo, userDetail, getUserInfo, getCurrentUserDetails } = useContext(User);
    const { streak, getStreak } = useContext(Streak);
    const { weather, getWeather } = useContext(Weather);
    const { intervalId, checkOffline } = useContext(Offline);
    const { appSettingMenus, getAppSettingMenus } = useContext(AppSettingMenu);
    const { systemVariables, getSystemVariables } = useContext(SystemVariable);
    const [anchorEl, setAnchorEl] = useState(null);
    const [mobileMoreAnchorEl, setMobileMoreAnchorEl] = useState(null);
    const [mode, setMode] = useState(theme ? theme : localStorage.getItem('theme'));
    const isMenuOpen = Boolean(anchorEl);
    const isMobileMenuOpen = Boolean(mobileMoreAnchorEl);

    const handleMobileMenuClose = () => {
        setMobileMoreAnchorEl(null);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        handleMobileMenuClose();
    };

    const handleMobileMenuOpen = event => {
        setMobileMoreAnchorEl(event.currentTarget);
    };

    const themeChange = event => {
        setMode(mode === 'light' ? 'dark' : 'light');
        if (mode === 'light') {
            setTheme("dark");
            localStorage.setItem("theme", JSON.stringify("dark"));
        }
        else {
            setTheme("light");
            localStorage.setItem("theme", JSON.stringify("light"));
        }
    };

    const goToProductionMode = () => {
        setTrainingMode(false);
    };

    const menuId = 'primary-search-account-menu';
    const renderMenu = (
        <Menu
            anchorEl={anchorEl}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            id={menuId}
            keepMounted
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            open={isMenuOpen}
            onClose={handleMenuClose}
        >
            <MenuItem onClick={handleMenuClose}>Profile</MenuItem>
            <MenuItem onClick={handleMenuClose}>My account</MenuItem>
        </Menu>
    );

    const mobileMenuId = 'mobileMenuBar';
    const renderMobileMenu = (
        <Menu
            anchorEl={mobileMoreAnchorEl}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            id={mobileMenuId}
            keepMounted
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            open={isMobileMenuOpen}
            onClose={handleMobileMenuClose}
            style={{ zIndex: '130' }}
        >
            <MenuItem>
                <UserAvatar theme={theme} themeChange={themeChange} mode={mode} />
            </MenuItem>
        </Menu>
    );

    useEffect(() => {
        if (!siteLoading) {
            if (trainingModules.length === 0) getTrainingModules();
            if (appSettingMenus.length === 0) getAppSettingMenus();
            if (userInfo === null) getUserInfo();
            if (userDetail === null) getCurrentUserDetails();
            if (streak.length === 0) getStreak();
            if (systemVariables.length === 0) getSystemVariables();
            if (weather.length === 0) getWeather(defaultSite.zipCode);
            if (intervalId === undefined) checkOffline();
        }
        // eslint-disable-next-line 
    }, [siteLoading]);

    useEffect(() => {
        if (userDetail !== null) {
            if (modules.length === 0)
                getUserModules(userDetail.PplPerson.toString());
        }
        // eslint-disable-next-line 
    }, [userDetail]);

    const currentTheme = React.useMemo(() => {
        return createMuiTheme(GlobalTheme[mode]);
    }, [mode]);

    return (
        <>
            <ThemeProvider theme={currentTheme}>
                <CssBaseline />
                <div>
                    <div className={classes.grow}>
                        {trainingMode && <div className={classes.banner}><Link onClick={goToProductionMode}>Training mode - Click here to return to Production</Link></div>}
                        <AppBar style={{ background: currentTheme.primary }} position="static">
                            <div>
                                <Toolbar>
                                    <Grid container direction="row"
                                        justify="space-between"
                                        alignItems="center">
                                        <Grid item>
                                            <label style={{ fontSize: "24px", fontWeight: '700', color: "#282E38", width: "120px", height: '31px', lineHeight: '31px' }}>TeamLead</label>
                                        </Grid>
                                        <Grid item>
                                            <SiteList />
                                        </Grid>
                                        <Grid item>
                                            <div className={classes.grow} />
                                            <div className={classes.sectionDesktop} >
                                                <UserAvatar theme={theme} themeChange={themeChange} mode={mode} />
                                            </div>
                                            <div className={classes.sectionMobile}>
                                                <IconButton
                                                    aria-label="show more" aria-controls={mobileMenuId} aria-haspopup="true"
                                                    onClick={handleMobileMenuOpen} color="inherit"
                                                >
                                                    <MoreIcon />
                                                </IconButton>
                                            </div>
                                        </Grid>
                                    </Grid>
                                </Toolbar>
                            </div>
                        </AppBar>
                        {renderMobileMenu}
                        {renderMenu}
                    </div>
                    <div className={classes.root}>
                        <Home mode={mode} theme={theme} modules={trainingMode ? trainingModules : modules} />
                    </div>
                </div>
            </ThemeProvider>
        </>
    );
}
export { MainLayout }
